import React, { useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Modal, Input, Typography } from "antd";
import { useTranslation } from "react-i18next";
import "./index.less";
const { Text } = Typography;

// 1) Polyfill Promise.withResolvers
if (typeof Promise !== "undefined" && typeof Promise.withResolvers === "undefined") {
  Promise.withResolvers = function () {
    let resolve, reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// 1) worker 版本/路径要与 pdfjs-dist 一致
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/js/pdf.worker.min.js`;

// 2) dataURL -> Uint8Array 转为稳定引用
function normalizePdfFile(file) {
  if (!file) return null;
  if (typeof file === "string" && file.startsWith("data:")) {
    const comma = file.indexOf(",");
    const base64 = file.slice(comma + 1);
    const bin = atob(base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return { data: bytes };
  }
  // URL / Blob / {data,password} 直接返回
  return file;
}

export default function PdfViewer({ file, containerWidth = 800, containerHeight = 600, zoom = 1, loading = <div style={{ padding: 12 }}>加载中…</div>, fallbackNode, className = "", style }) {
  const [numPages, setNumPages] = useState(null);
  const [fallback, setFallback] = useState(false);
  const { t } = useTranslation();
  // 弹窗/状态
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [pwd, setPwd] = useState("");
  const [awaitingPassword, setAwaitingPassword] = useState(false);

  // 3) 保存 pdf.js 的 callback（必须用 ref）
  const callbackRef = useRef(null);

  // 4) 仅在成功解密后记住密码，避免错误密码循环
  const savedPasswordRef = useRef("");

  // 5) 稳定化 file 引用，并在已有正确密码时直接附带
  const normalizedFile = useMemo(() => {
    const base = normalizePdfFile(file);
    if (base && typeof base === "object" && "data" in base) {
      if (savedPasswordRef.current) {
        return { ...base, password: savedPasswordRef.current };
      }
    }
    return base;
  }, [file]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    // 成功加载 => 说明密码正确，此时关闭弹窗并记住密码
    if (passwordVisible) {
      setPasswordVisible(false);
      setPasswordError("");
      setAwaitingPassword(false);
      if (pwd) savedPasswordRef.current = pwd; // 仅在成功时记住
      setPwd("");
    }
  };

  const onPassword = (callback, reason) => {
    // 已有正确密码 => 直接用它，不弹窗
    if (savedPasswordRef.current) {
      callback(savedPasswordRef.current);
      return;
    }
    // 保存当前回调，保持弹窗打开
    callbackRef.current = callback;
    setPasswordError(reason === 2 ? "密码错误，请重试" : "");
    setPasswordVisible(true);
    setAwaitingPassword(true); // 等待用户输入期间不显示 loading
  };

  const confirmPassword = () => {
    if (!pwd) return;
    const cb = callbackRef.current;
    if (!cb) {
      // 回调缺失（可能因为 Document 被重挂），直接报错占位
      console.error("Password callback is missing.");
      setFallback(true);
      return;
    }
    // 关键点：不关闭弹窗；交给 pdf.js 校验
    cb(pwd);
    // 如果错误，pdf.js 会再次触发 onPassword(reason=2)，我们更新红字但保持弹窗；
    // 如果正确，会触发 onLoadSuccess，这里关闭弹窗并记住密码。
  };

  const cancelPassword = () => {
    setPasswordVisible(false);
    setPwd("");
    setPasswordError("");
    setAwaitingPassword(false);
    setFallback(true); // 用户取消 => 显示占位，不再系统 prompt
  };

  const onLoadError = (err) => {
    console.error("PDF 加载失败：", err);
    setFallback(true);
    setPasswordVisible(false);
    setAwaitingPassword(false);
  };

  const defaultFallback = (
    <div
      style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#888",
        flexDirection: "column",
        gap: 8,
        padding: 16,
      }}
    >
      <img src={`${process.env.PUBLIC_URL}/images/document-doc.svg`} alt="doc" style={{ width: 64, height: 64 }} />
      <div>无法预览（可能需要密码或文件损坏）</div>
    </div>
  );

  if (fallback || !normalizedFile) {
    return fallbackNode ?? defaultFallback;
  }

  return (
    <div
      className={className}
      style={{
        width: containerWidth,
        height: containerHeight,
        overflow: "auto",
        border: "1px solid #eee",
        borderRadius: 8,
        ...style,
      }}
    >
      <Document file={normalizedFile} loading={awaitingPassword ? null : loading} className="width100" onLoadSuccess={onDocumentLoadSuccess} onLoadError={onLoadError} onPassword={onPassword}>
        {numPages &&
          Array.from({ length: numPages }).map((_, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={containerWidth}
              scale={zoom} // 缩放只影响 Page，不改变 Document props
            />
          ))}
      </Document>

      {/* AntD 密码弹窗（错误时保持打开并提示） */}
      <Modal open={passwordVisible} title={t("translation:admin.pdflock.title")} onCancel={cancelPassword} onOk={confirmPassword} okText={t("common:confirm")} cancelText={t("common:cancel")} destroyOnClose maskClosable={false} className="previewPasswordPdf-wrapper">
        <div style={{ display: "grid", gap: 8 }} autoComplete="off">
          <div type="secondary" className="label">
            {t("translation:admin.pdflock.label")}
          </div>
          <Input.Password className="preview-password-pdf-input" autoFocus value={pwd} placeholder={t("translation:admin.pdflock.password")} name="pdf-pass" autoComplete="off" onChange={(e) => setPwd(e.target.value)} onPressEnter={confirmPassword} />
          {passwordError && <Text type="danger">{passwordError}</Text>}
        </div>
      </Modal>
    </div>
  );
}
